import styled from 'styled-components';
import { MenuSeparator } from 'common/ui/MenuSeparator/index';
import { Colors } from 'styles';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import { Stack } from 'common/ui/Layout';
import { PicklistProvider, usePicklistEditor } from '../../Template/usePicklist';
import { RichTextEditor } from '../../RichTextEditor/ui/RichTextEditor';
import { useCallback, useEffect, useState } from 'react';
import useUpdateEffect from 'react-use/esm/useUpdateEffect';
import type { Dispatch, SetStateAction } from 'react';
import { Button } from 'common/ui/Button';
import { FieldBuilderPanePicklistTable } from './FieldBuilderPane.Picklist.Table';
import {
  Editor,
  Transforms,
  ReactEditor,
  Node,
  Element,
  NodeEntry,
  useSlateStatic,
} from 'domains/reporter/RichTextEditor/core';
import type { FieldBuilderFormErrors, FieldBuilderForm } from './types';
import type {
  ReportPicklistBeforeSubmissionOptions,
  PicklistPluginElement,
} from '../../RichTextEditor/plugins/picklist/types';
import { normalizeText } from 'domains/reporter/RichTextEditor/plugins/headingError/utils';
import { nanoid } from 'nanoid';
import { insertSelectedPicklistItem } from 'domains/reporter/RichTextEditor';
import { PICKLIST_PLUGIN_ID } from '../../RichTextEditor/plugins/picklist/types';
import { ParagraphPlugin } from '../../RichTextEditor/plugins/paragraph/paragraphPlugin';
import { LineBreakPlugin } from '../../RichTextEditor/plugins/lineBreak/lineBreakPlugin';
import { ExpandSelectionPlugin } from '../../RichTextEditor/plugins/expandSelection/expandSelectionPlugin';
import { logger } from 'modules/logger';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import {
  asPicklistOptionText,
  picklistTextToEditableFragment,
  isBlankPicklistOptionText,
  cloneNodeFragment,
} from '../../RichTextEditor/plugins/picklist/utils';
import { SlateContextProvider } from '../SlateContext';
import { rootTheme } from 'theme/rootTheme';

const Section = styled.div`
  padding: 24px;
  color: ${Colors.gray9};
`;

// A blank picklist option needs to have a space in order to validate and save on the backend.
export const DEFAULT_BLANK_PICKLIST_OPTION = ' ';

const ButtonGroup = styled.div`
  button:last-child {
    margin-left: 12px;
  }
`;

const PicklistAddButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-left: 8px;
  align-items: center;
`;

const isSlateContentNotEmpty = (content: Node[]): boolean => {
  return content.some((node) => {
    return node != null && Node.string(node).trim() !== '';
  });
};

const BLANK_OPTION_FRAGMENT: Node[] = [
  {
    text: DEFAULT_BLANK_PICKLIST_OPTION.trim(),
  },
];

const getBlankOptionFragment = (): Node[] => cloneNodeFragment(BLANK_OPTION_FRAGMENT);

const toEditableFragment = (
  text: ReportPicklistBeforeSubmissionOptions['text'] | undefined
): Node[] => picklistTextToEditableFragment(text, BLANK_OPTION_FRAGMENT);

export const emptyParagraph: Node[] = [
  {
    type: 'paragraph',
    children: [
      {
        text: '',
      },
    ],
  },
] as const;

export const FieldBuilderPanePicklist = ({
  form,
  setForm,
  formErrors,
  setFormErrors,
}: {
  form: FieldBuilderForm;
  formErrors: FieldBuilderFormErrors;
  setForm: Dispatch<SetStateAction<FieldBuilderForm>>;
  setFormErrors: Dispatch<SetStateAction<FieldBuilderFormErrors>>;
}): React.ReactElement => {
  const editor = useSlateStatic();
  const [picklistValueEditor, setPicklistValueEditor] = useState<Editor | null>(null);
  const [isTextEditorFocused, setIsTextEditorFocused] = useState(false);
  const [isEditingOption, setIsEditingOption] = useState(false);
  const { activePicklist, updatePicklist } = usePicklistEditor();
  const activePicklistId = activePicklist?.id;
  const [picklistValueEditorKey, setPicklistValueEditorKey] = useState(0); // triggers rerender of editor when editing / creating a picklist option
  const [editOptionId, setEditOptionId] = useState('');
  const [hasDefaultBlankOption, setHasDefaultBlankOption] = useState(false);

  const resetPicklistOptionEditor = useCallback(() => {
    setEditOptionId('');
    setIsEditingOption(false);
    setForm((prevForm) => ({
      ...prevForm,
      picklistForm: {
        ...prevForm.picklistForm,
        name: '',
        value: emptyParagraph,
      },
    }));
    setFormErrors((prevFormErrors) => ({
      ...prevFormErrors,
      picklistForm: {
        ...prevFormErrors.picklistForm,
        name: '',
        value: '',
      },
    }));
    setPicklistValueEditorKey((key) => key + 1);
  }, [setForm, setFormErrors]);

  useUpdateEffect(() => {
    resetPicklistOptionEditor();
  }, [activePicklistId, resetPicklistOptionEditor]);

  useEffect(() => {
    const firstOption = activePicklist?.options?.[0];
    if (firstOption == null) {
      setHasDefaultBlankOption(false);
      return;
    }
    setHasDefaultBlankOption(
      isBlankPicklistOptionText(firstOption.text, DEFAULT_BLANK_PICKLIST_OPTION)
    );
  }, [activePicklist?.options]);

  // Insert the default picklist (first option) on every picklist change
  useEffect(() => {
    if (
      editor == null ||
      activePicklist == null ||
      activePicklist.options.length === 0 ||
      activePicklist.id == null
    )
      return;

    // Find the picklist node by its ID instead of relying on selection
    // This ensures it works even when focus is elsewhere (e.g., on the Add button)
    const picklistNodes = Editor.nodes(editor, {
      at: [],
      match: (node) =>
        Element.isElement(node) &&
        node.type === PICKLIST_PLUGIN_ID &&
        node.picklistID === activePicklist.id,
    });

    const picklistEntry = picklistNodes.next().value as
      | NodeEntry<PicklistPluginElement>
      | undefined;

    if (picklistEntry) {
      const defaultOptionText =
        activePicklist.options.find((option) => option.default === true)?.text ??
        activePicklist.options[0]?.text;
      insertSelectedPicklistItem(editor, picklistEntry, defaultOptionText);
    }
  }, [activePicklist?.options, activePicklist?.id, editor, activePicklist]);

  const validatePicklist = useCallback(
    (pendingName: string, editOptionId?: string) => {
      const hasValue = isSlateContentNotEmpty(form.picklistForm.value);
      const hasName = pendingName.trim().length > 0;
      const isDuplicateName =
        editOptionId === undefined &&
        hasName &&
        activePicklist?.options != null &&
        activePicklist?.options.find(
          (option) => normalizeText(String(option.name ?? '')) === normalizeText(pendingName)
        ) != null;

      setFormErrors((prevForm) => ({
        ...prevForm,
        picklistForm: {
          ...prevForm.picklistForm,
          name: hasName
            ? isDuplicateName
              ? 'Picklist name must be unique.'
              : ''
            : 'Enter a name for the picklist option.',
          value: hasValue ? '' : 'Enter option text.',
        },
      }));

      if (!hasName || isDuplicateName || !hasValue) {
        logger.info('[FieldBuilderPane] Invalid picklist option submitted', {
          name: form.picklistForm.name ?? '',
          isDuplicateName,
          value: form.picklistForm.value ?? '',
        });
      }

      return hasName && isDuplicateName !== true && hasValue;
    },
    [activePicklist?.options, form.picklistForm.name, form.picklistForm.value, setFormErrors]
  );

  const createNewPicklistOption = useCallback(
    (
      pendingName: string,
      isBlankOption: boolean,
      editOptionId?: string
    ): Nullable<ReportPicklistBeforeSubmissionOptions> => {
      return isBlankOption || validatePicklist(pendingName, editOptionId)
        ? {
            id: nanoid(),
            name: pendingName,
            text: isBlankOption
              ? asPicklistOptionText(getBlankOptionFragment())
              : asPicklistOptionText(form.picklistForm.value),
            default: false,
          }
        : null;
    },
    [form.picklistForm.value, validatePicklist]
  );

  // Saves to Picklist Provider state
  const handleSavePicklistOption = useCallback(
    (name: string, isBlankOption: boolean, editOptionId?: string) => {
      const maybePicklistOption = createNewPicklistOption(name, isBlankOption, editOptionId);

      if (maybePicklistOption == null || activePicklist == null) return;

      let updatedOptions: ReportPicklistBeforeSubmissionOptions[] = [];
      if (isBlankOption) {
        updatedOptions = [
          { ...maybePicklistOption, default: true },
          ...activePicklist.options.map((option) => ({ ...option, default: false })),
        ];
      } else if (editOptionId !== undefined) {
        updatedOptions = [...activePicklist.options].map((option, index) =>
          option.id === editOptionId ? { ...option, ...maybePicklistOption } : option
        );
      } else {
        updatedOptions = [...activePicklist.options, { ...maybePicklistOption }].map(
          (option, index) => ({ ...option, default: index === 0 })
        );
      }

      updatePicklist({
        picklistID: activePicklist.id,
        options: updatedOptions,
      });

      resetPicklistOptionEditor();

      logger.info('[FieldBuilderPane] Picklist option saved', {
        option: maybePicklistOption,
        isBlankOption,
      });
    },
    [activePicklist, createNewPicklistOption, resetPicklistOptionEditor, updatePicklist]
  );

  const handleClickAddPicklist = () => {
    const pendingName = String(form.picklistForm.name).trim();
    handleSavePicklistOption(pendingName, false);
  };

  const handleRemovePicklistOption = useCallback(
    (picklistOptionId: string) => {
      if (activePicklist == null) return;

      updatePicklist({
        picklistID: activePicklist.id,
        options: [...activePicklist.options.filter((option) => option.id !== picklistOptionId)],
      });

      logger.info('[FieldBuilderPane] Picklist option removed', {
        picklistID: activePicklist.id,
        picklistOptionID: picklistOptionId,
      });

      resetPicklistOptionEditor();
    },
    [activePicklist, resetPicklistOptionEditor, updatePicklist]
  );

  const handleOnFocus = useCallback(() => {
    if (picklistValueEditor != null) {
      ReactEditor.focus(picklistValueEditor);
      Transforms.select(
        picklistValueEditor,
        picklistValueEditor.selection ?? Editor.end(picklistValueEditor, [])
      );
    }

    setIsTextEditorFocused(true);
  }, [picklistValueEditor]);

  const handleToggleDefaultBlankOption = useCallback(
    (hasBlankOption: boolean) => {
      if (hasBlankOption) {
        handleSavePicklistOption(DEFAULT_BLANK_PICKLIST_OPTION, true);
      } else {
        const firstOptionId = activePicklist?.options[0]?.id;
        if (firstOptionId) {
          handleRemovePicklistOption(firstOptionId);
        }
      }

      logger.info('[FieldBuilderPane] Toggled default blank option', {
        picklistID: activePicklist?.id,
        value: hasBlankOption,
      });
    },
    [
      activePicklist?.id,
      activePicklist?.options,
      handleSavePicklistOption,
      handleRemovePicklistOption,
    ]
  );

  const handleEditPicklistOption = useCallback(
    (id: string) => {
      const { name, text } = activePicklist?.options.find((option) => option.id === id) ?? {};

      setEditOptionId(id);
      setIsEditingOption(true);

      setForm((prevForm) => ({
        ...prevForm,
        picklistForm: {
          ...prevForm.picklistForm,
          name: name ?? '',
          value: toEditableFragment(text),
        },
      }));
      setPicklistValueEditorKey((key) => key + 1);

      logger.info('[FieldBuilderPane] Picklist option edit button clicked', {
        optionID: id,
        name,
        text,
      });
    },
    [activePicklist?.options, setForm]
  );

  const handleUpdatePicklistOption = useCallback(() => {
    setIsEditingOption(false);

    const pendingName = String(form.picklistForm.name).trim();
    handleSavePicklistOption(pendingName, false, editOptionId);
    logger.info('[FieldBuilderPane] Picklist option add button clicked', {
      name: pendingName,
    });
  }, [editOptionId, form.picklistForm.name, handleSavePicklistOption]);

  const handleCancelPicklistOption = useCallback(() => {
    logger.info('[FieldBuilderPane] Picklist option cancel button clicked', {
      optionID: editOptionId,
    });

    resetPicklistOptionEditor();
  }, [editOptionId, resetPicklistOptionEditor]);

  const handleSetAsDefault = useCallback(
    (id: string) => {
      if (activePicklist == null) return;
      logger.info('[FieldBuilderPane] Picklist option set as default', {
        optionID: id,
      });

      const hasDefaultBlankOption =
        activePicklist.options.length > 0
          ? isBlankPicklistOptionText(activePicklist.options[0].text, DEFAULT_BLANK_PICKLIST_OPTION)
          : false;
      updatePicklist({
        picklistID: activePicklist.id,
        options: [...activePicklist.options]
          .map((option) => {
            return {
              ...option,
              default: option.id === id,
            };
          })
          .filter((option) =>
            hasDefaultBlankOption ? option.id !== activePicklist.options[0].id : true
          ),
      });
    },
    [activePicklist, updatePicklist]
  );

  const handlePicklistNameInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) {
        return;
      }

      // `Enter` in Name moves focus to Value editor.
      e.preventDefault();
      e.stopPropagation();
      handleOnFocus();
    },
    [handleOnFocus]
  );

  return (
    <>
      <MenuSeparator />
      <Section>
        <Stack vertical>
          <Text css={{ paddingLeft: '12px', marginTop: '12px' }} variant="caption">
            Name
          </Text>
          <Input
            data-testid="picklist-name"
            pattern=".*\S.*"
            title="Enter a name for the picklist choice"
            autoComplete="off"
            placeholder="Enter a name for the picklist choice"
            css={{
              marginTop: '4px',
              backgroundColor: Colors.gray2,
              marginBottom: '8px',
              borderColor: formErrors.picklistForm.name !== '' ? Colors.red6 : Colors.gray8,
            }}
            value={form.picklistForm.name}
            onChange={(e) => {
              setForm((prevForm) => ({
                ...prevForm,
                picklistForm: {
                  ...prevForm.picklistForm,
                  name: e.target.value,
                },
              }));
            }}
            onKeyDown={handlePicklistNameInputKeyDown}
          />
          {formErrors.picklistForm.name !== '' && (
            <Text
              variant="caption"
              css={{
                marginTop: 4,
                marginLeft: 12,
                color: Colors.red6,
              }}
              data-testid="picklist-name-error"
            >
              {formErrors.picklistForm.name}&nbsp;
            </Text>
          )}
        </Stack>
        <Stack vertical stretchX css="height: 100%">
          <Text
            variant="caption"
            css={{
              marginLeft: 12,
            }}
          >
            Value
          </Text>
          <div
            css={{
              marginTop: 2,
              padding: 0,
            }}
          >
            <PicklistProvider>
              <div
                onFocus={handleOnFocus}
                onClick={handleOnFocus}
                onBlur={() => {
                  setIsTextEditorFocused(false);
                }}
                css={`
                  color: ${Colors.gray10};
                  border-radius: ${rootTheme.radius.sm}px;
                  border: 1px solid
                    ${formErrors.picklistForm?.value !== ''
                      ? Colors.red6
                      : isTextEditorFocused
                        ? Colors.mainBlue
                        : Colors.gray8};
                  padding: 12px 15px;
                  background-color: ${Colors.gray2};
                  box-sizing: border-box;
                  overflow: auto;
                  height: 120px;
                  position: relative;
                `}
                data-testid="picklist-text-editor"
              >
                <SlateContextProvider
                  key={`picklist-editor-${picklistValueEditorKey}`}
                  plugins={[ParagraphPlugin(), LineBreakPlugin(), ExpandSelectionPlugin()]}
                  initialValue={form.picklistForm.value}
                >
                  <RichTextEditor
                    variant="fragment"
                    enableDefaultSelection={false}
                    onChange={(value) => {
                      setForm((prevForm) => ({
                        ...prevForm,
                        picklistForm: {
                          ...prevForm.picklistForm,
                          value,
                        },
                      }));
                    }}
                    initialValue={form.picklistForm.value}
                    placeholder="Enter text that will show in the field bracket"
                    navMountPoint={null}
                    style={{
                      height: 240,
                      borderRadius: rootTheme.radius.sm,
                      backgroundColor: Colors.gray2,
                      boxSizing: 'border-box',
                      zIndex: 12,
                      width: 'auto',
                    }}
                    onInit={({ editor, pluginsBag }) => {
                      setPicklistValueEditor(editor);
                      ReactEditor.blur(editor);
                    }}
                  />
                </SlateContextProvider>
              </div>
            </PicklistProvider>
            {formErrors.picklistForm?.value !== '' && (
              <Text
                variant="caption"
                css={{
                  marginTop: 4,
                  marginLeft: 12,
                  color: Colors.red6,
                }}
                data-testid="picklist-text-error"
              >
                {formErrors.picklistForm?.value}&nbsp;
              </Text>
            )}
          </div>
          <Stack
            alignX="end"
            css={{
              marginTop: 8,
            }}
          >
            <PicklistAddButtonGroup>
              <FormControlLabel
                data-testid="picklist-blank-default-checkbox"
                css={{ marginTop: '4px' }}
                value={hasDefaultBlankOption}
                checked={hasDefaultBlankOption}
                control={
                  <Checkbox
                    color="primary"
                    onChange={(e) => {
                      handleToggleDefaultBlankOption(e.target.checked);
                    }}
                  />
                }
                label={<span css="font-size: 14px;">Add blank default option</span>}
              />
              {isEditingOption ? (
                <ButtonGroup>
                  <Button
                    type="button"
                    data-testid="cancel-picklist-item"
                    onClick={handleCancelPicklistOption}
                    variant="ghost"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    data-testid="update-picklist-item"
                    onClick={handleUpdatePicklistOption}
                    variant="primary"
                  >
                    Update
                  </Button>
                </ButtonGroup>
              ) : (
                <Button
                  type="button"
                  data-testid="add-picklist-item"
                  onClick={handleClickAddPicklist}
                  variant="primary"
                >
                  Add
                </Button>
              )}
            </PicklistAddButtonGroup>
          </Stack>
          <FieldBuilderPanePicklistTable
            formErrors={formErrors}
            handleRemovePicklistOption={handleRemovePicklistOption}
            handleEditPicklistOption={handleEditPicklistOption}
            handleSetAsDefault={handleSetAsDefault}
            editOptionId={editOptionId}
          />
        </Stack>
      </Section>
    </>
  );
};
